import CategoryIcon from 'owa-categories/lib/components/CategoryIcon';
import { observer } from 'owa-mobx-react';
import { getDensityModeCssClass, getDensityModeString } from 'owa-fabric-theme';
import React from 'react';
import { isReadingPanePositionOff, isSingleLineListView } from 'owa-mail-layout';
/* eslint-disable-next-line @fluentui/react-components/prefer-fluentui-v9 -- (https://aka.ms/OWALintWiki)
 * Baseline. DO NOT COPY AND PASTE! Substitute this line with an explanation or resolve the problem by using v9 components instead
 *	> Avoid using Icon from '@fluentui/react', as this package has already migrated to Fluent UI 9. Use a JSX SVG icon from '@fluentui/react-icons' instead. */
import { Icon } from '@fluentui/react/lib/Icon';
import { default as ChevronDown } from 'owa-fluent-icons-svg/lib/icons/ChevronDownRegular';
import { default as ChevronRight } from 'owa-fluent-icons-svg/lib/icons/ChevronRightRegular';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import { type SortColumn, getIsSearchTableShown } from 'owa-mail-list-store';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { showMailItemContextMenu } from 'owa-mail-list-actions/lib/actions/itemContextMenuActions';
import MailListContextMenuSource from 'owa-mail-list-store/lib/store/schema/MailListContextMenuSource';
import MailListContextMenuType from 'owa-mail-list-store/lib/store/schema/MailListContextMenuType';
import { getAnchorForContextMenu } from 'owa-positioning';
import { Spinner, Button } from '@fluentui/react-components';
import { useKeydownHandler } from 'owa-hotkeys';
import { MoreHorizontalRegular, MoreHorizontalFilled, bundleIcon } from '@fluentui/react-icons';
import loc from 'owa-localize';
import { headerActionMenu } from './MailListGroupHeader.locstring.json';

import {
    full,
    fullTypeRamp,
    medium,
    compact,
    outerContainer,
    outerContainerWithActions,
    outerContainerBleedthrough,
    groupHeaderClickable,
    scl,
    senderImageOn as styles_senderImageOn,
    selectionModeActivated,
    iconContainer,
    categoryIcon,
    groupHeaderAction,
    groupHeaderActionInProgress,
    spinner,
    moreButton,
    moreIcon,
    sclMoreButton,
    mclMoreButton,
    innerTextContainer,
    groupHeaderFocusOutline,
    mediumTypeRamp,
} from './MailListGroupHeader.scss';

import classnames from 'owa-classnames';
import {
    toggleVirtualizedGroupHeaderCollapse,
    virtualizedMailGroupHeadersStore,
} from 'owa-virtualized-group-headers';
import { singleSelectRow } from 'owa-mail-actions/lib/singleSelectRow';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { useIsTypeRampUpdatesEnabled } from 'owa-mail-list-item-shared/lib/utils/useIsTypeRampUpdatesEnabled';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';

// Constants for padding calculations
const PADDING_VALUES = {
    SCL: {
        full: {
            withChevron: { withSenderOrSelection: 40, withoutSender: 10 },
            withoutChevron: { withSenderOrSelection: 72, withoutSender: 40 },
        },
        medium: {
            withChevron: { withSenderOrSelection: 40, withoutSender: 14 },
            withoutChevron: { withSenderOrSelection: 68, withoutSender: 40 },
        },
        compact: {
            withChevron: { withSenderOrSelection: 38, withoutSender: 14 },
            withoutChevron: { withSenderOrSelection: 64, withoutSender: 40 },
        },
    },
    MCL: {
        withChevron: 38,
        withoutChevron: { full: 68, other: 64 },
    },
};

const MoreHorizontal = bundleIcon(MoreHorizontalFilled, MoreHorizontalRegular);

/**
 * Calculates the left padding for the group header based on various UI state conditions
 */
const calculateGroupHeaderPaddingLeft = (
    isSCL: boolean,
    densityModeString: 'full' | 'medium' | 'compact',
    senderImageEnabled: boolean,
    isChevronOrSpinnerVisible: boolean,
    isSelectionModeActivated: boolean
): number => {
    if (isSCL) {
        const paddingConfig = PADDING_VALUES.SCL[densityModeString];

        if (!paddingConfig) {
            return 0;
        }

        const chevronConfig = isChevronOrSpinnerVisible
            ? paddingConfig.withChevron
            : paddingConfig.withoutChevron;

        return senderImageEnabled || isSelectionModeActivated
            ? chevronConfig.withSenderOrSelection
            : chevronConfig.withoutSender;
    } else {
        // MCL (Multi-Column Layout)
        if (isChevronOrSpinnerVisible) {
            return PADDING_VALUES.MCL.withChevron;
        } else {
            return densityModeString === 'full'
                ? PADDING_VALUES.MCL.withoutChevron.full
                : PADDING_VALUES.MCL.withoutChevron.other;
        }
    }
};

export interface MailListGroupHeaderProps {
    headerText: string;
    tableView: TableView;
    onCollapse: () => void;
}

const MailListGroupHeader: React.FunctionComponent<MailListGroupHeaderProps> = observer(
    function MailListGroupHeader(props: MailListGroupHeaderProps) {
        const { headerText, tableView, onCollapse } = props;
        const elementRef = React.useRef<HTMLDivElement | null>(null);
        const isSCL = !isSingleLineListView();
        const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
        const densityModeCssClass = getDensityModeCssClass(
            isTypeRampUpdatesEnabled ? fullTypeRamp : full,
            isTypeRampUpdatesEnabled ? mediumTypeRamp : medium,
            compact
        );
        const densityModeString = getDensityModeString();
        const senderImageEnabled = isSenderImageEnabled();
        const isTableInSelectionMode = tableView?.isInCheckedMode || tableView?.isInSelectAllState;
        const isSelectionModeActivated = getIsSearchTableShown() || isTableInSelectionMode;
        const mailboxInfo = getMailboxInfo(tableView);

        const rowId = 'groupHeader' + headerText;

        const groupHeaderData =
            virtualizedMailGroupHeadersStore().virtualizedGroupHeaders.get(headerText);

        const isLoadingOfRowKeysComplete = groupHeaderData?.isLoadingOfRowKeysComplete ?? false;

        const areHeaderActionsEnabled =
            (!isUnifiedInboxMailboxInfo(mailboxInfo) && isLoadingOfRowKeysComplete) ||
            isGlobalFeatureEnabled('tri-group-header-actions-dev');

        const isGroupHeaderLoading =
            isGlobalFeatureEnabled('tri-group-header-actions-dev') &&
            groupHeaderData &&
            !groupHeaderData.isLoadingOfRowKeysComplete &&
            groupHeaderData.isLoadingForAction;

        const isChevronOrSpinnerVisible =
            isLoadingOfRowKeysComplete ||
            (isGlobalFeatureEnabled('tri-group-header-actions-dev') && isGroupHeaderLoading);

        const firstRowKey = groupHeaderData?.rowKeys[0];

        const headerClassNames = React.useMemo(
            () =>
                classnames(
                    outerContainer,
                    areHeaderActionsEnabled && outerContainerWithActions,
                    isTypeRampUpdatesEnabled && outerContainerBleedthrough,
                    isLoadingOfRowKeysComplete && groupHeaderClickable,
                    areHeaderActionsEnabled &&
                        (isGroupHeaderLoading ? groupHeaderActionInProgress : groupHeaderAction),
                    isReadingPanePositionOff()
                        ? 'singleLineCirclePersonaDivWidth'
                        : 'threeColumnCirclePersonaDivWidth',
                    densityModeCssClass,
                    groupHeaderFocusOutline
                ),
            [
                isLoadingOfRowKeysComplete,
                areHeaderActionsEnabled,
                isGroupHeaderLoading,
                densityModeCssClass,
            ]
        );

        const onVirtualizedHeaderClick = React.useCallback(
            (evt: React.MouseEvent<HTMLElement> | KeyboardEvent) => {
                toggleVirtualizedGroupHeaderCollapse(headerText);

                if (groupHeaderData?.isCollapsed && firstRowKey) {
                    // If the group header is collapsed, select the first row in the group
                    // and focus on the header to keep focus on what's visible.
                    singleSelectRow(
                        tableView,
                        firstRowKey,
                        false /* isUserNavigation */,
                        MailListItemSelectionSource.MailListGroupHeader,
                        evt.timeStamp,
                        headerText
                    );
                }

                onCollapse();
            },
            [onCollapse, tableView, headerText, firstRowKey, groupHeaderData?.isCollapsed]
        );

        const getOnHeaderContextMenu = React.useCallback(
            (selectionSource: MailListItemSelectionSource) =>
                (evt: React.MouseEvent<HTMLElement> | KeyboardEvent) => {
                    evt.stopPropagation();
                    evt.preventDefault();

                    if (firstRowKey) {
                        singleSelectRow(
                            tableView,
                            firstRowKey,
                            false /* isUserNavigation */,
                            selectionSource,
                            evt.timeStamp,
                            headerText
                        );
                    }

                    showMailItemContextMenu(
                        getAnchorForContextMenu(evt),
                        MailListContextMenuType.All,
                        MailListContextMenuSource.MailListGroupHeader,
                        elementRef.current
                    );
                },
            [tableView, headerText, firstRowKey]
        );

        const iconClassName = classnames(
            iconContainer,
            isSCL && scl,
            senderImageEnabled && styles_senderImageOn,
            isSelectionModeActivated && selectionModeActivated,
            densityModeCssClass
        );

        const spinnerClassName = classnames(
            spinner,
            isSCL && scl,
            senderImageEnabled && styles_senderImageOn,
            isSelectionModeActivated && selectionModeActivated,
            densityModeCssClass
        );

        const groupHeaderChevronIcon = groupHeaderData?.isCollapsed ? ChevronRight : ChevronDown;

        const moreButtonClass = classnames(moreButton, isSCL ? sclMoreButton : mclMoreButton);

        const moreHorizontalIcon = React.useMemo(
            () => <MoreHorizontal className={classnames(densityModeCssClass, moreIcon)} />,
            [densityModeCssClass]
        );

        const outerContainerPaddingLeft = useComputedValue(() => {
            const paddingLeft = calculateGroupHeaderPaddingLeft(
                isSCL,
                densityModeString,
                senderImageEnabled,
                !!isChevronOrSpinnerVisible,
                isSelectionModeActivated
            );

            return {
                paddingLeft: `${paddingLeft}px`,
            };
        }, [
            isSCL,
            densityModeString,
            senderImageEnabled,
            isChevronOrSpinnerVisible,
            isSelectionModeActivated,
        ]);

        const onRightArrowExpand = React.useCallback(
            (evt: KeyboardEvent) => {
                if (groupHeaderData?.isCollapsed && isLoadingOfRowKeysComplete) {
                    onVirtualizedHeaderClick(evt);
                }
            },
            [groupHeaderData?.isCollapsed, isLoadingOfRowKeysComplete, onVirtualizedHeaderClick]
        );

        const onLeftArrowCollapse = React.useCallback(
            (evt: KeyboardEvent) => {
                if (!groupHeaderData?.isCollapsed && isLoadingOfRowKeysComplete) {
                    onVirtualizedHeaderClick(evt);
                }
            },
            [groupHeaderData?.isCollapsed, isLoadingOfRowKeysComplete, onVirtualizedHeaderClick]
        );

        useKeydownHandler(elementRef, 'enter', onVirtualizedHeaderClick);
        useKeydownHandler(elementRef, 'right', onRightArrowExpand);
        useKeydownHandler(elementRef, 'left', onLeftArrowCollapse);

        React.useEffect(() => {
            if (tableView?.headerToFocus === headerText && elementRef.current) {
                elementRef.current.focus();
            }
        }, [tableView?.headerToFocus, headerText]);

        return (
            <div
                id={rowId}
                role="button"
                tabIndex={-1}
                ref={elementRef}
                aria-label={headerText}
                className={headerClassNames}
                style={outerContainerPaddingLeft}
                onClick={isLoadingOfRowKeysComplete ? onVirtualizedHeaderClick : undefined}
                onContextMenu={
                    areHeaderActionsEnabled && !isGroupHeaderLoading && !getIsSearchTableShown()
                        ? getOnHeaderContextMenu(MailListItemSelectionSource.GroupHeaderContextMenu)
                        : undefined
                }
                aria-expanded={
                    groupHeaderData?.isCollapsed === undefined
                        ? undefined
                        : !groupHeaderData?.isCollapsed
                }
            >
                {isLoadingOfRowKeysComplete && (
                    <Icon className={iconClassName} iconName={groupHeaderChevronIcon} />
                )}
                {isGroupHeaderLoading && <Spinner className={spinnerClassName} />}
                {(tableView?.tableQuery as MailFolderTableQuery)?.sortBy?.sortColumn === 14 ? (
                    <CategoryIcon
                        categoryName={headerText}
                        mailboxInfo={mailboxInfo}
                        iconClassName={categoryIcon}
                    />
                ) : null}
                <div title={headerText} className={innerTextContainer}>
                    {headerText}
                </div>
                {areHeaderActionsEnabled && !isGroupHeaderLoading && !getIsSearchTableShown() && (
                    <Button
                        appearance="transparent"
                        className={moreButtonClass}
                        icon={moreHorizontalIcon}
                        aria-label={loc(headerActionMenu)}
                        tabIndex={-1}
                        onClick={getOnHeaderContextMenu(
                            MailListItemSelectionSource.MailListGroupHeaderEllipsis
                        )}
                    />
                )}
            </div>
        );
    },
    'MailListGroupHeader'
);
export default MailListGroupHeader;
